// Player system: an invisible Arcade body (PLAYER_BODY, constant across armour)
// driven by the pure MovementController, the layered MechView, two weapon
// runtimes, stats/buffs, shields, i-frames, death and ability motion overrides.
//
// Frame order inside update(): physics has already stepped this frame, so the
// body's contact flags and velocity are fresh → movement controller → events
// and FX → view (pose at the body) → weapons (muzzle from the posed view) →
// reticle.

import Phaser from 'phaser';
import { DEPTH, PLAYER_BODY, ULTIMATE_COST } from '../../config/constants';
import { COLORS } from '../../config/palette';
import type {
  GameContext,
  MotionOverride,
  PlayerApi,
  PlayerStateId,
  Vec,
  WeaponRuntimeApi,
} from '../../core/contracts';
import { Subscriptions } from '../../core/events';
import type { DamageInfo, MechDef, MechId, MovementProfile, StatBlock, TrailKind, WeaponInstance } from '../../core/types';
import { art } from '../../art/registry';
import { MECHS } from '../../data/mechs';
import { EQUIPMENT, WEAPONS } from '../../data/items';
import { createWeaponRuntime } from '../combat/weapons';
import { MovementController, NO_INPUT, type ControlInput, type ControlSensors } from './controller';
import { MechView } from './MechView';
import { FEEL, approach, clamp, deriveTuning, jumpPhase, landingSquash } from './movement';
import { Reticle } from './reticle';
import { applyBerserk, berserkActive, finalizeStats, tickBuffs, type Buff, type BuffMod } from './stats';

/** Weapon draw delay after a switch (s): the new gun cannot fire until it is up. */
const DRAW_DELAY = 0.12;
/** Facing flips once the aim is this far (px) past the body centre (no jitter when aiming straight up). */
const FACE_HYSTERESIS = 8;
/** Aim origin above the feet (≈ shoulder height). */
const AIM_ORIGIN_Y = 60;
/** Dash afterimage interval (s). */
const AFTERIMAGE_INTERVAL = 0.03;
/** Visual dash trail segment width (px). */
const TRAIL_WIDTH = 16;
/** Death: player:died fires after the animation, never later than this (s). */
const DEATH_MAX = 1.2;
const DEATH_MIN = 0.7;
/** Run speed below which the mech reads as standing. */
const RUN_THRESHOLD = 30;
/** Landing / jump events are ignored right after spawning (the body settles onto the floor). */
const SPAWN_GRACE = 0.2;
/** PHASE_CAPACITOR: shock applied to enemies dashed through. */
const PHASE_SHOCK = { id: 'shock', stacks: 2, duration: 3 } as const;
/** Post-hit flicker frequency (Hz). */
const FLICKER_HZ = 16;

// Air-jump tricks (cosmetic; the hitbox never rotates).
/** Horizontal speed (× runSpeed) toward the facing at which an air jump becomes a superman dive. */
const SUPERMAN_SPEED = 0.85;
/** Superman forward pitch (rad) at the threshold speed and at very high speed. */
const SUPERMAN_PITCH_MIN = 1.0;
const SUPERMAN_PITCH_MAX = 1.35;
/** The dive straightens up once falling faster than this (px/s), ready to land. */
const SUPERMAN_END_FALL = 220;
/** Front flip duration bounds (s); the flip aims to finish near the air jump's apex. */
const FLIP_MIN = 0.34;
const FLIP_MAX = 0.55;

type AirTrick = 'none' | 'flip' | 'superman';

const warned = new Set<string>();
/** Other systems may still be stubs that throw: degrade gracefully and warn once per call site. */
function safe<T>(label: string, fn: () => T, fallback: T): T {
  try {
    return fn();
  } catch (err) {
    if (!warned.has(label)) {
      warned.add(label);
      console.warn(`[player] ${label} unavailable:`, err);
    }
    return fallback;
  }
}

function weaponSignature(inst: WeaponInstance): string {
  return `${inst.uid}|${inst.defId}|${inst.modSlots}|${inst.mods.map((m) => m.uid).join(',')}`;
}

class Player implements PlayerApi {
  readonly mechId: MechId;
  readonly def: MechDef;
  readonly movement: MovementProfile;
  readonly bodyObject: Phaser.GameObjects.Zone;
  readonly body: Phaser.Physics.Arcade.Body;
  readonly view: MechView;

  hp: number;
  shield: number;
  ultCharge: number;

  private readonly ctx: GameContext;
  private readonly scene: Phaser.Scene;
  private readonly controller: MovementController;
  private readonly reticle: Reticle;
  private readonly subs = new Subscriptions();
  private readonly buffs = new Map<string, Buff>();
  private collider: Phaser.Physics.Arcade.Collider | null = null;

  private _stats: StatBlock;
  private berserkOn = false;
  private started = false;
  private destroyed = false;

  private _facing: 1 | -1 = 1;
  private _aimAngle = 0;
  private controlEnabled = true;
  private dead = false;
  private deathT = 0;
  private diedEmitted = false;
  private deathSparkT = 0;

  private _sinceDamaged = 999;
  private hitInvuln = 0;
  private shieldBroken = false;
  private flickerT = 0;

  private runtimes: (WeaponRuntimeApi | null)[] = [null, null];
  private signatures: (string | null)[] = [null, null];
  private _activeIndex = 0;
  private drawTimer = 0;

  private age = 0;
  private squash = 0;
  private trick: AirTrick = 'none';
  private trickT = 0;
  /** Flip duration (s). */
  private trickDur = 0;
  /** Superman dive pitch (rad). */
  private trickPitch = 0;
  /** Spin the current flip started from. */
  private flipFrom = 0;
  private trickDir: 1 | -1 = 1;
  private spin = 0;
  private lastRunFrame = -1;
  private afterimageT = 0;
  private trailX = 0;
  private trailY = 0;
  private dashX0 = 0;
  private dashY0 = 0;
  private phaseCapDash = false;
  private readonly shocked = new Set<number>();
  private glideFxT = 0;

  private readonly inp: ControlInput = { moveX: 0, jumpHeld: false, jumpAge: Infinity, downHeld: false, dashAge: Infinity };
  private readonly sensors: ControlSensors = {
    onGround: false,
    onOneWay: false,
    blockedLeft: false,
    blockedRight: false,
    blockedUp: false,
    vx: 0,
    vy: 0,
  };
  private readonly muzzleScratch = { x: 0, y: 0, angle: 0 };

  constructor(ctx: GameContext, spawn: Vec) {
    this.ctx = ctx;
    this.scene = ctx.scene;
    this.mechId = ctx.run.mechId;
    this.def = MECHS[this.mechId];
    this.movement = this.def.movement;

    // Physics body: feet on spawn.y, centred on spawn.x.
    const zone = this.scene.add.zone(spawn.x, spawn.y - PLAYER_BODY.h / 2, PLAYER_BODY.w, PLAYER_BODY.h);
    this.scene.physics.add.existing(zone);
    this.bodyObject = zone;
    this.body = zone.body as Phaser.Physics.Arcade.Body;
    this.body.setSize(PLAYER_BODY.w, PLAYER_BODY.h);
    this.body.setAllowGravity(true);
    this.body.setCollideWorldBounds(false);
    this.body.setGravityY(this.movement.gravity);

    // Stats from the run inventory (pure data on RunState; flags are added in start()).
    this._stats = this.computeStats(false);
    this.controller = new MovementController(deriveTuning(this.movement, this._stats));

    const run = ctx.run;
    this.hp = clamp(Number.isFinite(run.hp) ? run.hp : this._stats.maxHp, 1, this._stats.maxHp);
    this.shield = clamp(Number.isFinite(run.shield) ? run.shield : this._stats.maxShield, 0, this._stats.maxShield);
    this.ultCharge = clamp(Number.isFinite(run.ultCharge) ? run.ultCharge : 0, 0, ULTIMATE_COST);

    this.view = new MechView(this.scene, this.mechId);
    this.view.setDepth(DEPTH.PLAYER);
    this.view.setReactorColor(this.def.reactorColor);
    this.view.setPosition(spawn.x, spawn.y);
    this.view.play('idle');
    const inv = run.inventory;
    this.view.setEquipment(safe('inventory.visualIds', () => inv.visualIds(), []));
    const w0 = inv.weapons[inv.activeWeapon] ?? inv.weapons.find((w) => !!w) ?? null;
    this.view.setWeapon(w0 ? w0.defId : null);
    this._activeIndex = w0 ? inv.weapons.indexOf(w0) : 0;

    this.reticle = new Reticle(this.scene, this.def.reactorColor);
  }

  // ── Read-only state ──

  get x(): number {
    return this.body.center.x;
  }
  get y(): number {
    return this.body.center.y;
  }
  get feetY(): number {
    return this.body.bottom;
  }
  get vx(): number {
    return this.body.velocity.x;
  }
  get vy(): number {
    return this.body.velocity.y;
  }
  get facing(): 1 | -1 {
    return this._facing;
  }
  get aimAngle(): number {
    return this._aimAngle;
  }
  get onGround(): boolean {
    return this.controller.onGround;
  }
  get alive(): boolean {
    return !this.dead;
  }
  get stats(): Readonly<StatBlock> {
    return this._stats;
  }
  get sinceDamaged(): number {
    return this._sinceDamaged;
  }
  get dashCooldownRemaining(): number {
    return this.controller.dashCooldown;
  }
  get weapons(): readonly (WeaponRuntimeApi | null)[] {
    return this.runtimes;
  }
  get activeWeaponIndex(): number {
    return this._activeIndex;
  }
  /** Ability override flagged ghost: enemies should skip contact damage (not part of the contract). */
  get isGhost(): boolean {
    return !!this.controller.override?.ghost;
  }

  get invulnerable(): boolean {
    return this.dead || this.hitInvuln > 0 || this.controller.iframes > 0 || !!this.controller.override?.invulnerable;
  }

  get state(): PlayerStateId {
    if (this.dead) return 'dead';
    if (!this.controlEnabled) return 'locked';
    if (this.controller.override) return 'override';
    if (this.controller.dashing) return 'dash';
    if (!this.controller.onGround) return 'air';
    return Math.abs(this.body.velocity.x) > RUN_THRESHOLD ? 'run' : 'idle';
  }

  activeWeapon(): WeaponRuntimeApi | null {
    return this.runtimes[this._activeIndex] ?? null;
  }

  muzzle(): { x: number; y: number; angle: number } {
    const m = this.view.muzzleWorld();
    // Shots always travel toward the aim, whatever pose the arms are in.
    m.angle = this._aimAngle;
    return m;
  }

  isDashing(): boolean {
    return this.controller.dashing;
  }

  // ── Lifecycle ──

  start(): void {
    if (this.destroyed) return;
    this.started = true;
    this.collider = safe(
      'world.addCollider',
      () => this.ctx.world.addCollider(this.bodyObject, { canDrop: () => this.controller.dropTime > 0 }),
      null,
    );
    this.subs
      .on('inventory:changed', () => {
        this.refreshLoadout();
        this.refreshStats();
      })
      .on('player:shieldBreak', () => {
        this.shieldBroken = true;
      })
      .on('synergy:activated', () => this.refreshStats())
      .on('synergy:deactivated', () => this.refreshStats())
      .on('weapon:fired', (e) => {
        if (e.secondary) return;
        const active = this.activeWeapon();
        if (active && active.def.id === e.weapon) this.view.recoil(clamp(0.45 + (WEAPONS[e.weapon]?.recoil ?? 0.3), 0, 1));
      });
    this.refreshLoadout();
    this.refreshStats();
  }

  update(dt: number): void {
    if (this.destroyed) return;
    this.age += dt;
    this._sinceDamaged += dt;
    this.hitInvuln = Math.max(0, this.hitInvuln - dt);
    this.drawTimer = Math.max(0, this.drawTimer - dt);
    if (tickBuffs(this.buffs, dt)) this.refreshStats();
    this.checkBerserk();

    if (this.dead) {
      this.updateDeath(dt);
      return;
    }

    this.updateMovement(dt);
    this.updateFacingAndAim();
    this.updateShield(dt);
    this.updateView(dt);
    this.updateWeapons(dt);
    this.updateReticle(dt);
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.subs.dispose();
    try {
      this.collider?.destroy();
    } catch {
      // Physics world already torn down with the scene.
    }
    this.collider = null;
    for (const w of this.runtimes) {
      try {
        w?.destroy();
      } catch {
        // ignore
      }
    }
    this.runtimes = [null, null];
    this.view.destroy();
    this.reticle.destroy();
    this.bodyObject.destroy();
  }

  // ── Movement ──

  private updateMovement(dt: number): void {
    const input = this.ctx.input;
    const b = this.body;
    const inp = this.inp;
    const control = this.controlEnabled;
    if (control) {
      inp.moveX = input.moveX;
      inp.jumpHeld = input.held('jump');
      inp.jumpAge = input.sincePressed('jump');
      inp.downHeld = input.held('down') || input.moveY > 0.5;
      inp.dashAge = input.sincePressed('dash');
    }
    const s = this.sensors;
    s.onGround = b.blocked.down || b.touching.down;
    s.onOneWay = s.onGround && this.standingOnOneWay();
    s.blockedLeft = b.blocked.left || b.touching.left;
    s.blockedRight = b.blocked.right || b.touching.right;
    s.blockedUp = b.blocked.up || b.touching.up;
    s.vx = b.velocity.x;
    s.vy = b.velocity.y;

    // PHASE_CAPACITOR lengthens the next dash's i-frames.
    if (control && inp.dashAge <= FEEL.dashBuffer) {
      this.phaseCapDash = this.hasFlag('PHASE_CAPACITOR');
      this.controller.dashIFrameBonus = this.phaseCapDash ? FEEL.phaseCapacitorIFrames : 0;
    }

    const wasOverride = !!this.controller.override;
    const out = this.controller.step(control ? inp : NO_INPUT, s, this._facing, dt);
    if (out.consumeJump) input.consume('jump');
    if (out.consumeDash) input.consume('dash');

    b.setVelocity(out.vx, out.vy);
    b.setGravityY(out.gravity);
    b.maxVelocity.set(Math.max(6000, this.controller.tuning.dashSpeed * 1.5), Math.max(out.maxFall, Math.abs(out.vy) + 1));

    const settled = this.age > SPAWN_GRACE;
    const fx = this.ctx.fx;
    const reactor = this.def.reactorColor;
    const feetY = this.feetY;
    const x = this.x;

    if (out.jumped !== 0) {
      const air = out.jumped === 2;
      this.ctx.bus.emit('player:jump', { air, x, y: feetY });
      this.sfx(air ? 'double_jump' : 'jump');
      this.squash = FEEL.jumpStretch;
      if (air) {
        this.startTrick(out.vx);
        safe('fx.ring', () => fx.ring(x, feetY, 26, { color: reactor, width: 3 }), undefined);
        safe('fx.sparks', () => fx.sparks(x, feetY, Math.PI / 2, { count: 7, color: reactor, speed: 260, spread: 1.4 }), undefined);
      } else {
        safe('fx.smoke', () => fx.smoke(x, feetY, { count: 3, scale: 0.5 }), undefined);
      }
    }
    if (out.dropped) this.squash = FEEL.jumpStretch * 0.5;
    if (out.landed && settled) {
      const impact = out.landImpact;
      this.ctx.bus.emit('player:land', { impact, x, y: feetY });
      const heavy = impact >= FEEL.heavyLanding;
      if (impact > 150) {
        this.sfx(heavy ? 'land_heavy' : 'land', { volume: clamp(impact / 1200, 0.35, 1) });
        this.squash = landingSquash(impact, this.controller.tuning.maxFall);
        safe('fx.smoke', () => fx.smoke(x, feetY, { count: heavy ? 6 : 3, scale: heavy ? 0.8 : 0.5 }), undefined);
      }
      if (heavy) {
        safe('fx.shake', () => fx.shake(0.0035, 110), undefined);
        safe('fx.debris', () => fx.debris(x, feetY, { count: 5, speed: 260 }), undefined);
      }
    }
    if (out.dashStarted) this.onDashStart();
    if (this.controller.dashing) this.updateDashFx(dt);
    if (out.dashEnded) this.onDashEnd();
    if (out.overrideEnded || (wasOverride && !this.controller.override)) this.lastRunFrame = -1;

    // Glide: faint reactor sparks streaming below the feet.
    if (this.controller.gliding) {
      this.glideFxT -= dt;
      if (this.glideFxT <= 0) {
        this.glideFxT = 0.07;
        safe('fx.sparks', () => fx.sparks(x, feetY + 2, Math.PI / 2, { count: 2, color: reactor, speed: 160, spread: 0.6, life: 0.25 }), undefined);
      }
    }
  }

  private standingOnOneWay(): boolean {
    const w = this.ctx.world;
    const y = this.body.bottom + 3;
    const half = PLAYER_BODY.w / 2 - 3;
    const cx = this.body.center.x;
    return safe(
      'world.isOneWayAt',
      () => (w.isOneWayAt(cx, y) || w.isOneWayAt(cx - half, y) || w.isOneWayAt(cx + half, y)) && !w.isSolidAt(cx, y),
      false,
    );
  }

  private onDashStart(): void {
    const dir = this.controller.dashDir;
    this.dashX0 = this.x;
    this.dashY0 = this.y;
    this.trailX = this.x;
    this.trailY = this.feetY - 14;
    this.afterimageT = 0;
    this.shocked.clear();
    this._facing = dir;
    this.ctx.bus.emit('player:dash', { x: this.x, y: this.y, dir });
    this.sfx('dash');
    const fx = this.ctx.fx;
    const reactor = this.def.reactorColor;
    const x = this.x - dir * 16;
    const y = this.y + 6;
    safe('fx.sparks', () => fx.sparks(x, y, dir > 0 ? Math.PI : 0, { count: 9, color: reactor, speed: 420, spread: 0.7 }), undefined);
    if (this.controller.onGround) safe('fx.smoke', () => fx.smoke(x, this.feetY, { count: 3, scale: 0.55 }), undefined);
  }

  private updateDashFx(dt: number): void {
    const fx = this.ctx.fx;
    this.afterimageT -= dt;
    if (this.afterimageT <= 0) {
      this.afterimageT += AFTERIMAGE_INTERVAL;
      safe('fx.afterimage', () => fx.afterimage(this.view.root, { color: this.def.reactorColor, alpha: 0.5 }), undefined);
    }
    const kind = this.dashTrailKind();
    if (kind) {
      const tx = this.x;
      const ty = this.feetY - 14;
      if (Math.abs(tx - this.trailX) + Math.abs(ty - this.trailY) > 6) {
        const x0 = this.trailX;
        const y0 = this.trailY;
        safe('fx.trailSegment', () => fx.trailSegment(x0, y0, tx, ty, kind, TRAIL_WIDTH), undefined);
        this.trailX = tx;
        this.trailY = ty;
      }
    }
    if (this.phaseCapDash) this.phaseShock();
  }

  private onDashEnd(): void {
    this.ctx.bus.emit('player:dashEnd', { x0: this.dashX0, y0: this.dashY0, x1: this.x, y1: this.y });
  }

  /** Dash trail of the equipped legs module (art visuals first, item data as fallback). */
  private dashTrailKind(): TrailKind | null {
    const legs = this.ctx.run.inventory.equipment.legs;
    if (!legs) return null;
    return art.visuals[legs.defId]?.dashTrail ?? EQUIPMENT[legs.defId]?.dashTrail ?? null;
  }

  /** PHASE_CAPACITOR: enemies whose hit areas overlap the body during a dash get 2 shock stacks (once per dash). */
  private phaseShock(): void {
    const list = safe('enemies.list', () => this.ctx.enemies.list, []);
    const b = this.body;
    for (const e of list) {
      if (!e.alive || e.spawning || e.kind === 'prop' || this.shocked.has(e.uid)) continue;
      const areas = safe('enemy.hitAreas', () => e.hitAreas(), []);
      for (const a of areas) {
        if (a.x < b.right && a.x + a.w > b.left && a.y < b.bottom && a.y + a.h > b.top) {
          this.shocked.add(e.uid);
          safe('status.apply', () => this.ctx.status.apply(e, { ...PHASE_SHOCK }), undefined);
          safe('fx.sparks', () => this.ctx.fx.sparks(e.x, e.y, 0, { count: 8, color: COLORS.electric, speed: 300, spread: Math.PI * 2 }), undefined);
          break;
        }
      }
    }
  }

  // ── Aim / facing ──

  private updateFacingAndAim(): void {
    const ox = this.x;
    const oy = this.feetY - AIM_ORIGIN_Y;
    const aim = this.ctx.input.aim(ox, oy);
    this._aimAngle = aim.angle;
    const c = this.controller;
    if (c.dashing || c.recovering) {
      this._facing = c.dashDir;
    } else if (aim.x < ox - FACE_HYSTERESIS) {
      this._facing = -1;
    } else if (aim.x > ox + FACE_HYSTERESIS) {
      this._facing = 1;
    }
  }

  // ── Shields ──

  private updateShield(dt: number): void {
    const s = this._stats;
    if (this.shield >= s.maxShield || this._sinceDamaged < s.shieldDelay) return;
    this.shield = Math.min(s.maxShield, this.shield + s.shieldRegen * dt);
    if (this.shield >= s.maxShield && this.shieldBroken) {
      this.shieldBroken = false;
      this.ctx.bus.emit('player:shieldRestored', {});
      this.sfx('shield_regen', { volume: 0.6 });
    }
  }

  // ── Air tricks ──

  /** Air jump: a superman dive when launching fast toward the facing, otherwise a front flip. */
  private startTrick(vx: number): void {
    const t = this.controller.tuning;
    const speed = Math.abs(vx) / Math.max(1, t.runSpeed);
    const dir: 1 | -1 = Math.abs(vx) > RUN_THRESHOLD ? (vx > 0 ? 1 : -1) : this._facing;
    this.trickDir = dir;
    this.trickT = 0;
    if (speed >= SUPERMAN_SPEED && dir === this._facing) {
      this.trick = 'superman';
      // Faster launches dive flatter.
      this.trickPitch = SUPERMAN_PITCH_MIN + (SUPERMAN_PITCH_MAX - SUPERMAN_PITCH_MIN) * clamp((speed - SUPERMAN_SPEED) / 0.6, 0, 1);
    } else {
      this.trick = 'flip';
      // One turn by roughly the apex of the jump (rise time = v / g).
      this.trickDur = clamp((t.airJumpVelocity / Math.max(1, t.gravity)) * 1.1, FLIP_MIN, FLIP_MAX);
      // Start from any lingering dive tilt so there is no visible snap.
      this.flipFrom = this.spin;
    }
  }

  /** Advance the current trick; returns the body spin (world rad, clockwise +). */
  private updateTrick(dt: number): number {
    const c = this.controller;
    // Grounded, dashing, ability motions and death always read upright.
    if (c.onGround || c.dashing || c.recovering || c.override || this.dead) {
      this.trick = 'none';
      this.spin = 0;
      return 0;
    }
    this.trickT += dt;
    if (this.trick === 'flip') {
      const p = Math.min(1, this.trickT / Math.max(1e-3, this.trickDur));
      // Fast tuck then a soft finish.
      const e = 1 - Math.pow(1 - p, 2.2);
      this.spin = this.flipFrom + (this.trickDir * Math.PI * 2 - this.flipFrom) * e;
      if (p >= 1) {
        this.trick = 'none';
        this.spin = 0;
      }
    } else if (this.trick === 'superman') {
      // Physics ends the dive: falling, slowing down, or turning to aim behind.
      const speed = Math.abs(this.vx) / Math.max(1, c.tuning.runSpeed);
      if (this.vy > SUPERMAN_END_FALL || speed < SUPERMAN_SPEED * 0.6 || this._facing !== this.trickDir) {
        this.trick = 'none';
      } else {
        const target = this.trickDir * this.trickPitch;
        this.spin = approach(this.spin, target, 9 * dt);
      }
    }
    if (this.trick === 'none') {
      // Settle upright (after a dive).
      this.spin *= Math.exp(-dt * 12);
      if (Math.abs(this.spin) < 0.01) this.spin = 0;
    }
    return this.spin;
  }

  // ── View ──

  private updateView(dt: number): void {
    const v = this.view;
    const c = this.controller;
    v.setPosition(this.x, this.feetY);
    v.setFacing(this._facing);
    v.setAim(this._aimAngle);
    const ov = c.override;
    if (ov) {
      v.setPhase(ov.anim ?? 'dash', c.overrideProgress);
    } else if (c.dashing || c.recovering) {
      v.setPhase('dash', c.dashProgress);
    } else if (!c.onGround) {
      v.setPhase('jump', jumpPhase(this.vy, c.sinceTakeoff, c.tuning));
    } else {
      const vx = this.vx;
      if (Math.abs(vx) > RUN_THRESHOLD) {
        // Backpedal plays the run cycle in reverse when moving against the facing.
        const k = clamp(Math.abs(vx) / Math.max(1, c.tuning.runSpeed), 0.55, 1.4);
        v.play('run', { timeScale: Math.sign(vx) === this._facing ? k : -k * 0.85 });
      } else {
        v.play('idle');
      }
    }

    this.squash *= Math.exp(-dt * 13);
    if (Math.abs(this.squash) < 0.002) this.squash = 0;
    v.setSquash(this.squash);
    v.setSpin(this.updateTrick(dt));
    v.setKick(this.activeWeapon()?.kick ?? 0);

    // Post-hit invulnerability flicker (dash i-frames read through the dash pose and afterimages instead).
    if (this.hitInvuln > 0 && c.iframes <= 0) {
      this.flickerT += dt;
      v.setAlpha(Math.sin(this.flickerT * Math.PI * 2 * FLICKER_HZ) > 0 ? 1 : 0.35);
    } else {
      this.flickerT = 0;
      v.setAlpha(1);
    }
    v.update(dt);
    this.footsteps();
  }

  private footsteps(): void {
    const v = this.view;
    if (v.currentAnim !== 'run' || !this.controller.onGround) {
      this.lastRunFrame = -1;
      return;
    }
    const f = v.frameIndex;
    if (f === this.lastRunFrame) return;
    const half = Math.floor(v.frameCount / 2);
    if (this.lastRunFrame !== -1 && (f === 0 || f === half)) {
      this.sfx('footstep', { volume: 0.32, pitch: 0.92 + Math.random() * 0.16 });
    }
    this.lastRunFrame = f;
  }

  // ── Weapons ──

  private updateWeapons(dt: number): void {
    const input = this.ctx.input;
    if (this.controlEnabled) {
      if (input.pressed('weapon1')) this.switchTo(0);
      else if (input.pressed('weapon2')) this.switchTo(1);
      else if (input.pressed('weaponNext') || input.pressed('weaponPrev')) this.switchTo(this.otherFilledSlot());
    }
    const trigger = this.controlEnabled && this.drawTimer <= 0 && input.held('fire');
    for (let i = 0; i < this.runtimes.length; i++) {
      const w = this.runtimes[i];
      if (!w) continue;
      if (i === this._activeIndex) {
        this.view.muzzleInto(this.muzzleScratch);
        const m = { x: this.muzzleScratch.x, y: this.muzzleScratch.y, angle: this._aimAngle };
        safe('weapon.update', () => w.update(dt, trigger, m), undefined);
      } else {
        safe('weapon.updateHolstered', () => w.updateHolstered(dt), undefined);
      }
    }
  }

  private otherFilledSlot(): number {
    const n = this.runtimes.length;
    for (let k = 1; k < n; k++) {
      const i = (this._activeIndex + k) % n;
      if (this.ctx.run.inventory.weapons[i]) return i;
    }
    return this._activeIndex;
  }

  private switchTo(index: number, silent = false): void {
    const inv = this.ctx.run.inventory;
    if (index === this._activeIndex || index < 0 || index >= inv.weapons.length || !inv.weapons[index]) return;
    const prev = this.activeWeapon();
    if (prev) safe('weapon.onHolster', () => prev.onHolster(), undefined);
    this._activeIndex = index;
    inv.activeWeapon = index;
    const next = this.activeWeapon();
    if (next) safe('weapon.onDraw', () => next.onDraw(), undefined);
    this.drawTimer = DRAW_DELAY;
    const id = inv.weapons[index]?.defId ?? null;
    this.view.setWeapon(id);
    if (!silent) {
      this.sfx('weapon_switch');
      this.ctx.bus.emit('weapon:switched', { index, weapon: id });
    }
  }

  refreshLoadout(): void {
    if (this.destroyed) return;
    const inv = this.ctx.run.inventory;
    for (let i = 0; i < 2; i++) {
      const inst = inv.weapons[i] ?? null;
      const sig = inst ? weaponSignature(inst) : null;
      if (sig === this.signatures[i] && (this.runtimes[i] || !inst)) continue;
      const old = this.runtimes[i];
      if (old) safe('weapon.destroy', () => old.destroy(), undefined);
      this.runtimes[i] = inst && this.started ? safe('createWeaponRuntime', () => createWeaponRuntime(this.ctx, inst), null) : null;
      this.signatures[i] = this.runtimes[i] ? sig : null;
    }
    // Keep the held slot if it still has a gun, else move to one that does.
    if (!inv.weapons[this._activeIndex]) {
      const filled = inv.weapons.findIndex((w) => !!w);
      if (filled >= 0) this._activeIndex = filled;
    }
    inv.activeWeapon = this._activeIndex;
    this.view.setWeapon(inv.weapons[this._activeIndex]?.defId ?? null);
    this.view.setEquipment(safe('inventory.visualIds', () => inv.visualIds(), []));
  }

  // ── Stats ──

  private computeStats(withFlags: boolean): StatBlock {
    const run = this.ctx.run;
    const base = safe('inventory.computeStats', () => run.inventory.computeStats(this.def.base, this.def.kitTags), {
      ...this.def.base,
    });
    const s = finalizeStats(base, { buffs: this.buffs.values(), glassFrame: run.hasChallenge('glass_frame') });
    const hp = this.hp ?? Math.min(run.hp, s.maxHp);
    this.berserkOn = withFlags && berserkActive(this.hasFlag('BERSERK'), hp, s.maxHp);
    if (this.berserkOn) applyBerserk(s);
    return s;
  }

  refreshStats(): void {
    if (this.destroyed) return;
    this._stats = this.computeStats(this.started);
    this.hp = Math.min(this.hp, this._stats.maxHp);
    this.shield = Math.min(this.shield, this._stats.maxShield);
    this.controller.setTuning(deriveTuning(this.movement, this._stats));
  }

  private checkBerserk(): void {
    if (!this.started) return;
    const on = berserkActive(this.hasFlag('BERSERK'), this.hp, this._stats.maxHp);
    if (on !== this.berserkOn) this.refreshStats();
  }

  addBuff(id: string, mods: { stat: keyof StatBlock; op: 'add' | 'mul'; value: number }[], seconds: number): void {
    if (seconds <= 0) return;
    this.buffs.set(id, { id, mods: mods.map((m): BuffMod => ({ ...m })), remaining: seconds });
    this.refreshStats();
  }

  private hasFlag(flag: string): boolean {
    if (!this.started) return false;
    return safe('effects.hasFlag', () => this.ctx.effects.hasFlag(flag), false);
  }

  // ── Damage / health ──

  setInvulnerable(ms: number): void {
    this.hitInvuln = Math.max(this.hitInvuln, ms / 1000);
  }

  applyKnockback(vx: number, vy: number): void {
    if (this.dead || !this.controller.knockback()) return;
    this.body.setVelocity(vx, Math.min(vy, this.body.velocity.y));
  }

  heal(amount: number): void {
    if (this.dead || amount <= 0) return;
    const before = this.hp;
    this.hp = Math.min(this._stats.maxHp, this.hp + amount);
    const healed = this.hp - before;
    if (healed > 0) {
      this.ctx.bus.emit('player:healed', { amount: healed });
      this.view.flash(COLORS.heal, 120);
    }
  }

  notifyDamaged(_amount: number, _info: DamageInfo): void {
    if (this.dead) return;
    this._sinceDamaged = 0;
    if (this.shield <= 0 && this._stats.maxShield > 0) this.shieldBroken = true;
    this.view.flash(0xffffff, 90);
    this.view.flinch();
  }

  kill(): void {
    if (this.dead) return;
    this.dead = true;
    this.deathT = 0;
    this.deathSparkT = 0;
    this.hp = 0;
    this.controller.halt();
    this.view.setAlpha(1);
    this.view.play('death', { restart: true });
    this.reticle.setVisible(false);
    for (const w of this.runtimes) if (w) safe('weapon.onHolster', () => w.onHolster(), undefined);
    this.sfx('death_player');
    const fx = this.ctx.fx;
    safe('fx.sparks', () => fx.sparks(this.x, this.y - 10, -Math.PI / 2, { count: 18, color: this.def.reactorColor, speed: 420, spread: Math.PI }), undefined);
    safe('fx.flash', () => fx.flash(0xffffff, 0.25, 120), undefined);
    safe('fx.shake', () => fx.shake(0.006, 260), undefined);
  }

  private updateDeath(dt: number): void {
    this.deathT += dt;
    const b = this.body;
    const t = this.controller.tuning;
    b.setVelocity(approach(b.velocity.x, 0, t.groundDecel * 0.5 * dt), b.velocity.y);
    b.setGravityY(t.gravity * t.fallGravityMult);
    b.maxVelocity.y = t.maxFall;
    this.view.setPosition(this.x, this.feetY);
    this.view.update(dt);

    // Shorting sparks while the frame collapses.
    this.deathSparkT -= dt;
    if (this.deathT < 0.9 && this.deathSparkT <= 0) {
      this.deathSparkT = 0.11;
      const p = this.view.attachWorld('chest');
      safe('fx.sparks', () => this.ctx.fx.sparks(p.x, p.y, -Math.PI / 2, { count: 4, color: COLORS.electric, speed: 260, spread: 2 }), undefined);
    }
    if (!this.diedEmitted && ((this.view.animDone && this.deathT >= DEATH_MIN) || this.deathT >= DEATH_MAX)) {
      this.diedEmitted = true;
      const p = this.view.attachWorld('chest');
      safe('fx.explosion', () => this.ctx.fx.explosion(p.x, p.y, 70, this.def.reactorColor), undefined);
      safe('fx.smoke', () => this.ctx.fx.smoke(p.x, p.y, { count: 6, scale: 1 }), undefined);
      this.ctx.bus.emit('player:died', { x: this.x, y: this.y });
    }
  }

  // ── Control / abilities ──

  setControlEnabled(on: boolean): void {
    this.controlEnabled = on;
    if (!on) {
      this.inp.moveX = 0;
      this.inp.jumpHeld = false;
    }
  }

  overrideMotion(m: MotionOverride): void {
    if (this.dead) return;
    if (this.controller.startOverride(m)) this.onDashEnd();
  }

  cancelOverride(): void {
    this.controller.cancelOverride();
  }

  reduceDashCooldown(seconds: number): void {
    this.controller.reduceCooldown(seconds);
  }

  // ── Reticle / audio ──

  private updateReticle(dt: number): void {
    const p = this.ctx.input.pointerWorld;
    const w = this.activeWeapon();
    this.reticle.setVisible(this.controlEnabled);
    this.reticle.update(dt, p.x, p.y, w?.heat ?? 0, w?.overheated ?? false, w?.charge ?? 0, this.def.reactorColor);
  }

  private sfx(id: Parameters<GameContext['audio']['play']>[0], opts?: { volume?: number; pitch?: number }): void {
    safe(`audio.${id}`, () => this.ctx.audio.play(id, opts), undefined);
  }
}

export function createPlayer(ctx: GameContext, spawn: Vec): PlayerApi {
  return new Player(ctx, spawn);
}
